/**
 * Gift Wrap Greeting Input Component
 * Handles character counting and pre-fill from cart for product page greeting input
 */

// Gift wrap variant ID (matches theme settings)
// Use window property to prevent duplicate declaration errors if script loads multiple times
window.GIFT_WRAP_VARIANT_ID = window.GIFT_WRAP_VARIANT_ID || '56750549500288';

// Initialize character counters on page load
document.addEventListener('DOMContentLoaded', initializeGiftGreeting);

// Re-initialize after dynamic content loads (e.g., variant changes)
document.addEventListener('shopify:section:load', initializeGiftGreeting);

async function initializeGiftGreeting() {
  const greetingBlocks = document.querySelectorAll('[data-gift-greeting-block]');

  // Use for...of instead of forEach to properly await async operations
  for (const block of greetingBlocks) {
    const textarea = block.querySelector('[data-gift-greeting-input]');
    const charCountDisplay = block.querySelector('[data-char-count]');
    const counter = block.querySelector('[data-character-counter]');

    if (!textarea || !charCountDisplay) {
      continue;
    }

    const maxLength = parseInt(textarea.getAttribute('data-max-length')) || 320;
    const warningThreshold = Math.floor(maxLength * 0.9); // 90%

    // Remove existing listeners by cloning
    const newTextarea = textarea.cloneNode(true);
    textarea.parentNode.replaceChild(newTextarea, textarea);

    // Character counter update function
    function updateCharacterCount() {
      const length = newTextarea.value.length;
      charCountDisplay.textContent = length;

      // Add warning class when approaching limit
      if (counter) {
        if (length >= warningThreshold) {
          counter.classList.add('warning');
        } else {
          counter.classList.remove('warning');
        }
      }
    }

    // Attach event listener
    newTextarea.addEventListener('input', updateCharacterCount);

    // Pre-fill from cart if this is the gift wrap product page
    await preFillGreetingFromCart(newTextarea);

    // Initial count (after pre-fill)
    updateCharacterCount();
  }
}

/**
 * Pre-fill greeting textarea from cart if user is on gift wrap product page
 * @param {HTMLTextAreaElement} textarea - Greeting textarea element
 */
async function preFillGreetingFromCart(textarea) {
  try {
    // Check if we're on the gift wrap product page by checking URL params
    const urlParams = new URLSearchParams(window.location.search);
    const variantParam = urlParams.get('variant');

    // Only proceed if variant parameter matches gift wrap variant ID
    if (variantParam !== window.GIFT_WRAP_VARIANT_ID) {
      return;
    }

    // Fetch current cart to check for existing greeting
    const response = await fetch('/cart.js');
    if (!response.ok) {
      return;
    }

    const cart = await response.json();

    // Find gift wrap line item
    // Ensure both sides of comparison are strings
    const giftWrapItem = cart.items.find(item => {
      const itemVariantId = String(item.variant_id);
      const targetVariantId = String(window.GIFT_WRAP_VARIANT_ID);
      return itemVariantId === targetVariantId;
    });

    if (!giftWrapItem) {
      return;
    }

    // Extract existing greeting from properties
    const existingGreeting = giftWrapItem.properties?.Tervehdys || '';

    if (existingGreeting && existingGreeting.trim() !== '') {
      // Pre-fill textarea with existing greeting
      textarea.value = existingGreeting;

      // Announce to screen readers (accessibility)
      const block = textarea.closest('[data-gift-greeting-block]');
      const messageTemplate = block?.dataset?.prefilledMessage || 'Greeting prefilled: [count] characters';
      announceToScreenReader(messageTemplate.replace('[count]', existingGreeting.length));
    }

  } catch (error) {
    // Fail silently - pre-fill is a nice-to-have, not critical
    console.error('[Gift Greeting] Error pre-filling greeting from cart:', error);
  }
}

/**
 * Announce message to screen readers via ARIA live region
 * @param {string} message - Message to announce
 */
function announceToScreenReader(message) {
  const liveRegion = document.createElement('div');
  liveRegion.setAttribute('role', 'status');
  liveRegion.setAttribute('aria-live', 'polite');
  liveRegion.className = 'visually-hidden';
  liveRegion.textContent = message;

  document.body.appendChild(liveRegion);

  // Remove after announcement (1 second is enough for screen readers)
  setTimeout(() => {
    document.body.removeChild(liveRegion);
  }, 1000);
}
